import React, { useEffect, useRef, useState } from "react";
import { Search, SendHorizontal, X } from "lucide-react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export default function PageSearchInput({
  className,
  disabled = false,
  inputClassName,
  onSearch,
  placeholder = "Search…",
  testId,
  value = "",
}) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);
  const normalizedDraft = draft.trim();
  const normalizedValue = String(value || "").trim();
  const isApplied = Boolean(normalizedValue) && normalizedDraft === normalizedValue;
  const hasChangedDraft = Boolean(normalizedDraft) && normalizedDraft !== normalizedValue;
  const canReset = Boolean(normalizedValue) && !hasChangedDraft;

  useEffect(() => {
    setDraft(String(value || ""));
  }, [value]);

  const reset = () => {
    setDraft("");
    onSearch("");
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  const submit = () => {
    if (!normalizedDraft || !hasChangedDraft || disabled) return;
    setDraft(normalizedDraft);
    onSearch(normalizedDraft);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (canReset || (!normalizedDraft && normalizedValue)) reset();
    else submit();
  };

  return (
    <form onSubmit={handleSubmit} className={cn("relative min-w-0", className)} role="search">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#71817C]" />
      <Input
        ref={inputRef}
        data-testid={testId}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && normalizedValue) {
            event.preventDefault();
            reset();
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className={cn(
          "h-10 bg-white pl-9 pr-11 focus-visible:ring-0",
          inputClassName,
        )}
      />
      {(normalizedDraft || normalizedValue) && (
        <button
          type="submit"
          disabled={disabled}
          aria-label={canReset || (!normalizedDraft && normalizedValue) ? "Clear search" : "Submit search"}
          title={canReset || (!normalizedDraft && normalizedValue) ? "Clear search" : "Submit search"}
          className={cn(
            "absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E5B52] focus-visible:ring-offset-1 disabled:opacity-50",
            isApplied || (!normalizedDraft && normalizedValue)
              ? "bg-[#EDF4F1] text-[#0E5B52] hover:bg-[#E1EDE8]"
              : "bg-[#0E5B52] text-white shadow-sm hover:bg-[#0B4A43]",
          )}
        >
          {canReset || (!normalizedDraft && normalizedValue)
            ? <X className="h-3.5 w-3.5" />
            : <SendHorizontal className="h-3.5 w-3.5" />}
        </button>
      )}
    </form>
  );
}
